import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ViewChildren, QueryList, ViewChild } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule, MatTabChangeEvent } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LabpartnerService } from '../../services/labpartner.service';
import {
  Bundle,
  BundleAdvanced,
  BundleTestResultResponse,
  SavannaBundleTestResultResponse,
  SavannaQueryElasticRequest,
  SavannaTestResultFilters,
  Sofia2QueryElasticRequest,
  Sofia2TestResultFilters,
} from '../../services/labpartner.service.model';
import { UserAccountService } from '../../services/user-account.service';
import { DialogService } from '../../shared/dialog.service';
import { ExperimentService } from '../../shared/experiment.service';
import { NotificationService } from '../../shared/notification.service';
import { ColumnFilterComponent } from '../../column-filter/column-filter.component';
import { ColumnFilterService } from '../../column-filter/column-filter.service';

// Table column definition interface
interface TableColumn {
  key: string;
  label: string;
  sortable: boolean;
}

@Component({
  selector: 'app-advanced-bundle-edit',
  templateUrl: './advanced-bundle-edit.component.html',
  styleUrls: ['./advanced-bundle-edit.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatProgressBarModule,
    MatTabsModule,
    MatPaginatorModule,
    MatSortModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatCardModule,
    MatExpansionModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatTableModule,
    ColumnFilterComponent,
  ],
})
export class AdvancedBundleEditComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  bundleForm!: FormGroup;
  sofia2FiltersForm!: FormGroup;
  savannaFiltersForm!: FormGroup;
  bundleAdvanced!: BundleAdvanced;
  devices: any[] = [];
  selectedDeviceType: number = 0;
  isLoading: boolean = false;
  showResults: boolean = false;
  // Separate result sets per device type
  sofia2Results: BundleTestResultResponse[] = [];
  savannaResults: SavannaBundleTestResultResponse[] = [];
  isQueryingResults: boolean = false;
  selectedTabIndex: number = 0; // Default to tab 1 (Bundle Information)

  // Display properties for read-only fields
  bundleDateCreated: Date | null = null;
  bundleCreatedByName: string = '';

  // Search & sort
  searchQuery: string = '';
  // Filter to show/hide already added tests
  showAlreadyAddedTests: boolean = true;

  // Selection state per device type (for filter tables)
  selectedSofia2Ids: Set<string> = new Set<string>();
  selectedSavannaIds: Set<string> = new Set<string>();

  // ViewChild setters to link Material components to DataSources dynamically (handles *ngIf)
  @ViewChild('sofia2ResultsPaginator') set sofia2ResultsPaginator(p: MatPaginator) { if (p) this.sofia2ResultsDataSource.paginator = p; }
  @ViewChild('sofia2ResultsSort') set sofia2ResultsSort(s: MatSort) { if (s) this.sofia2ResultsDataSource.sort = s; }

  @ViewChild('savannaResultsPaginator') set savannaResultsPaginator(p: MatPaginator) { if (p) this.savannaResultsDataSource.paginator = p; }
  @ViewChild('savannaResultsSort') set savannaResultsSort(s: MatSort) { if (s) this.savannaResultsDataSource.sort = s; }

  @ViewChild('sofia2SelectedPaginator') set sofia2SelectedPaginator(p: MatPaginator) { if (p) this.sofia2SelectedTestsDataSource.paginator = p; }
  @ViewChild('sofia2SelectedSort') set sofia2SelectedSort(s: MatSort) { if (s) this.sofia2SelectedTestsDataSource.sort = s; }

  @ViewChild('savannaSelectedPaginator') set savannaSelectedPaginator(p: MatPaginator) { if (p) this.savannaSelectedTestsDataSource.paginator = p; }
  @ViewChild('savannaSelectedSort') set savannaSelectedSort(s: MatSort) { if (s) this.savannaSelectedTestsDataSource.sort = s; }

  @ViewChildren(ColumnFilterComponent) columnFilterQueryList?: QueryList<ColumnFilterComponent>;

  sofia2ResultsDataSource = new MatTableDataSource<BundleTestResultResponse>([]);
  savannaResultsDataSource = new MatTableDataSource<SavannaBundleTestResultResponse>([]);
  sofia2SelectedTestsDataSource = new MatTableDataSource<BundleTestResultResponse>([]);
  savannaSelectedTestsDataSource = new MatTableDataSource<SavannaBundleTestResultResponse>([]);

  // Selected tests arrays (maintained for compatibility/checking)
  sofia2SelectedTests: BundleTestResultResponse[] = [];
  savannaSelectedTests: SavannaBundleTestResultResponse[] = [];

  // Table names for filter service
  readonly resultsTableName = 'advanced_bundle_results';
  readonly selectedTableName = 'advanced_bundle_selected';

  // Selection state for selected tests tables (for bulk delete)
  selectedSofia2SelectedTestIds: Set<string> = new Set<string>();
  selectedSavannaSelectedTestIds: Set<string> = new Set<string>();

  // Use MatTableDataSource defaults for pagination
  pageSize: number = 10;
  pageSizeOptions: number[] = [10, 25, 50, 100];
  selectedTestsPageSize: number = 10;
  selectedTestsPageSizeOptions: number[] = [10, 25, 50, 100];

  // Pagination state (synced with MatPaginator)
  pageIndex: number = 0;
  selectedTestsPageIndex: number = 0;

  // Sorting state
  resultsSortState: Sort = { active: '', direction: '' };
  selectedTestsSortState: Sort = { active: '', direction: '' };

  // Search & sort for selected tests tables
  selectedTestsSearchQuery: string = ''; // Keep for syncing logic if needed


  // Column order for Sofia2 tables
  readonly sofia2Columns: TableColumn[] = [
    { key: 'instrumentSerialNumber', label: 'Instrument Serial Number', sortable: true },
    { key: 'assayId', label: 'Assay ID', sortable: true },
    { key: 'revision', label: 'Revision', sortable: true },
    { key: 'assayName', label: 'Assay Name', sortable: true },
    { key: 'testResultId', label: 'Test Result ID', sortable: true },
    { key: 'cassetteSerialNumber', label: 'Cassette Serial Number', sortable: true },
    { key: 'cassetteLotNumber', label: 'Cassette Lot Number', sortable: true },
    { key: 'dateTimeUtc', label: 'Date Time (UTC)', sortable: true },
    { key: 'dateTime', label: 'Date Time', sortable: true },
    { key: 'operatorId', label: 'Operator ID', sortable: true },
    { key: 'result', label: 'Result', sortable: true },
    { key: 'sampleId', label: 'Sample ID', sortable: true },
  ];

  // Column order for Savanna tables
  readonly savannaColumns: TableColumn[] = [
    { key: 'instrumentSerialNumber', label: 'Instrument Serial Number', sortable: true },
    { key: 'assayId', label: 'Assay ID', sortable: true },
    { key: 'revision', label: 'Revision', sortable: true },
    { key: 'assayName', label: 'Assay Name', sortable: true },
    { key: 'testResultId', label: 'Test Result ID', sortable: true },
    { key: 'cartridgeSerialNumber', label: 'Cartridge Serial Number', sortable: true },
    { key: 'cartridgeLotNumber', label: 'Cartridge Lot Number', sortable: true },
    { key: 'dateTimeUtc', label: 'Date Time (UTC)', sortable: true },
    { key: 'dateTime', label: 'Date Time', sortable: true },
    { key: 'operatorId', label: 'Operator ID', sortable: true },
    { key: 'result', label: 'Result', sortable: true },
    { key: 'sampleId', label: 'Sample ID', sortable: true },
  ];

  get sofia2ResultsDisplayedColumns(): string[] {
    return ['select', ...this.sofia2Columns.map(column => column.key), 'actions'];
  }

  get savannaResultsDisplayedColumns(): string[] {
    return ['select', ...this.savannaColumns.map(column => column.key), 'actions'];
  }

  get sofia2SelectedDisplayedColumns(): string[] {
    return ['select', ...this.sofia2Columns.map(column => column.key), 'actions'];
  }

  get savannaSelectedDisplayedColumns(): string[] {
    return ['select', ...this.savannaColumns.map(column => column.key), 'actions'];
  }

  // Helper to get the value of a property from a result
  getColumnValue(result: any, columnKey: string): any {
    return result[columnKey];
  }

  // Format date in UTC timezone explicitly
  formatUtcDate(date: Date | string | null | undefined): string {
    if (!date) return '-';

    try {
      // Convert to Date if it's a string
      const dateObj = date instanceof Date ? date : new Date(date);

      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        return '-';
      }

      // Format in UTC timezone
      return dateObj.toLocaleString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) + ' UTC';
    } catch (error) {
      console.error('Error formatting UTC date:', error, date);
      return '-';
    }
  }

  // Helper to get the label of a column (for special cases like cassetteLabel)
  getColumnLabel(column: TableColumn): string {
    if (column.key === 'cassetteSerialNumber' || column.key === 'cartridgeSerialNumber') {
      return this.cassetteSerialLabel;
    }
    if (column.key === 'cassetteLotNumber' || column.key === 'cartridgeLotNumber') {
      return this.cassetteLotLabel;
    }
    if (column.key === 'dateTime') {
      // Get local timezone offset and format it as UTC±HH:MM
      const offset = new Date().getTimezoneOffset();
      const offsetHours = Math.abs(Math.floor(offset / 60));
      const offsetMins = Math.abs(offset % 60);
      const sign = offset <= 0 ? '+' : '-';
      const timeZoneStr = `UTC${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMins.toString().padStart(2, '0')}`;
      return `Date Time (${timeZoneStr})`;
    }
    return column.label;
  }

  // Dynamic labels based on device type
  get cassetteLabel(): string {
    // Savanna (2) uses Cartridge, Sofia2 (1) uses Cassette
    return this.selectedDeviceType === 2 ? 'Cartridge' : 'Cassette';
  }

  get cassetteSerialLabel(): string {
    return this.selectedDeviceType === 2 ? 'Cartridge Serial Number' : 'Cassette Serial Number';
  }

  get cassetteLotLabel(): string {
    return this.selectedDeviceType === 2 ? 'Cartridge Lot' : 'Cassette Lot';
  }

  get cassetteSerialPlaceholder(): string {
    return `Enter ${this.cassetteLabel.toLowerCase()} serial numbers separated by comma, space, tab, or newline`;
  }

  get cassetteLotPlaceholder(): string {
    return `Enter ${this.cassetteLabel.toLowerCase()} lot`;
  }

  get deviceTypeName(): string {
    if (!this.selectedDeviceType || this.selectedDeviceType === 0) {
      return '';
    }
    return this.experimentService.getDeviceName(this.selectedDeviceType);
  }

  /**
   * Get the total count of tests added to selected tests arrays
   */
  getTotalSelectedTestsCount(): number {
    if (this.selectedDeviceType === 1) {
      return this.sofia2SelectedTests.length;
    } else if (this.selectedDeviceType === 2) {
      return this.savannaSelectedTests.length;
    }
    return 0;
  }

  /**
   * Get the label for the Selected Tests tab with count
   */
  get selectedTestsTabLabel(): string {
    const count = this.getTotalSelectedTestsCount();
    return `Selected Tests Results${count > 0 ? ` (${count})` : ''}`;
  }

  get bundleInformationTabLabel(): string {
    const deviceName = this.deviceTypeName;
    if (deviceName) {
      return `Bundle Information (${deviceName})`;
    }
    return 'Bundle Information';
  }

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private apiService: LabpartnerService,
    private dialogService: DialogService,
    private experimentService: ExperimentService,
    private notificationService: NotificationService,
    private accountService: UserAccountService,
    private columnFilterService: ColumnFilterService
  ) {
    // Only show Sofia2 (id: 1) and Savanna (id: 2) for Advanced Bundles
    this.devices = this.experimentService.devices.filter(
      device => device.id === 1 || device.id === 2,
    );
  }

  async ngOnInit(): Promise<void> {
    this.initializeForms();
    this.setupDataSources();
    this.setupColumnFilters();

    // Ensure user list is loaded before loading bundle data (for CreatedBy display)
    await this.accountService.SetUserList();
    this.loadBundleData();
  }

  // Setup filter predicates and accessors
  private setupDataSources(): void {
    // Helper to setup a datasource
    const setupDS = (ds: MatTableDataSource<any>, tableName: string) => {
      // Custom filter predicate to handle both global search and column filters
      ds.filterPredicate = (data: any, filter: string) => {
        const tableFilters = this.columnFilterService.getActiveTableFilters(tableName as any);
        const filteredColumns = this.columnFilterService.getTableFilterColumns(tableName as any);

        // Check column filters
        const columnFilterFn = () => {
          return filteredColumns
            .map(fc => {
              // Handle special cases or data mapping if needed, assuming data[fc] exists
              const val = data[fc];
              return tableFilters[fc].has(String(val));
            })
            .every(matchesFilters => matchesFilters);
        };

        // Check global search (filter string)
        const displayedColumnsFilterFn = () => {
          if (!filter || filter === 'custom-filter') return true; // 'custom-filter' is a hack to trigger predicate without actual search
          // Simple check: does any value in the object match the filter string?
          // Or use matchesSearch logic
          return this.matchesSearch(data, filter);
        };

        // Combine logic
        if (filteredColumns.length && filter && filter !== 'custom-filter') {
          return columnFilterFn() && displayedColumnsFilterFn();
        } else if (filteredColumns.length && (!filter || filter === 'custom-filter')) {
          return columnFilterFn();
        } else if (!filteredColumns.length && filter && filter !== 'custom-filter') {
          return displayedColumnsFilterFn();
        } else {
          return true;
        }
      };
    };

    setupDS(this.sofia2ResultsDataSource, this.resultsTableName);
    setupDS(this.savannaResultsDataSource, this.resultsTableName);
    setupDS(this.sofia2SelectedTestsDataSource, this.selectedTableName);
    setupDS(this.savannaSelectedTestsDataSource, this.selectedTableName);
  }

  private setupColumnFilters(): void {
    this.columnFilterService.tableFiltersUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tableName => {
        if (tableName === this.resultsTableName) {
          // Trigger filter update for result tables
          this.refreshResultFilters();
        } else if (tableName === this.selectedTableName) {
          this.onSelectedTestsSearchChange();
        }
      });

    this.columnFilterService.tableFiltersReset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(tableName => {
        if (tableName === this.resultsTableName || tableName === 'all') {
          // Clear logic if needed
          if (this.columnFilterQueryList) {
            this.columnFilterService.deleteQueryListFilters(this.columnFilterQueryList);
          }
        }
      });
  }

  // Update logic to assign data to MatTableDataSource
  // Update logic to assign data to MatTableDataSource
  updateDataSources(): void {
    // When results change
    this.sofia2ResultsDataSource.data = this.sofia2Results || [];
    this.savannaResultsDataSource.data = this.savannaResults || [];
    this.sofia2SelectedTestsDataSource.data = this.sofia2SelectedTests || [];
    this.savannaSelectedTestsDataSource.data = this.savannaSelectedTests || [];

    // Notify filters
    this.tableDataChanged();
  }

  // ngAfterViewInit removed or kept minimal if needed
  // ngAfterViewInit removed or kept minimal if needed
  ngAfterViewInit() {
    this.columnFilterQueryList?.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tableDataChanged();
    });
    // Initial trigger
    setTimeout(() => {
      this.tableDataChanged();
    });
  }

  // For now, let's assume we update data source data, and we need to trigger filter update
  // For now, let's assume we update data source data, and we need to trigger filter update
  tableDataChanged() {
    // Need to pass the correct datasource and search key to the service
    // Determining active table...
    let activeDS: MatTableDataSource<any> | null = null;
    let searchKey = '';
    let activeTableName = '';

    if (this.selectedTabIndex === 1) { // Results tab
      activeDS = this.selectedDeviceType === 1 ? this.sofia2ResultsDataSource : this.savannaResultsDataSource;
      searchKey = this.searchQuery;
      activeTableName = this.resultsTableName;
    } else if (this.selectedTabIndex === 2) { // Selected Tests tab
      activeDS = this.selectedDeviceType === 1 ? this.sofia2SelectedTestsDataSource : this.savannaSelectedTestsDataSource;
      searchKey = this.selectedTestsSearchQuery;
      activeTableName = this.selectedTableName;
    }

    if (activeDS && this.columnFilterQueryList && activeTableName) {
      // Filter the components to only those belonging to the active table
      const relevantFilters = this.columnFilterQueryList.filter(f => f.tableName === activeTableName);

      this.columnFilterService.refreshQueryListDisplayFilters(
        relevantFilters,
        activeDS,
        searchKey
      );
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    // Bundle form for editable fields
    this.bundleForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: ['', [Validators.maxLength(500)]],
    });

    // Sofia2 filters form for test results search
    this.sofia2FiltersForm = this.fb.group({
      instrumentSerialNumber: ['', [Validators.maxLength(2000)]], // Textarea: supports multiple values separated by comma, space, tab, or newline
      startDate: [''],
      endDate: [''],
      assayId: ['', [Validators.pattern(/^\d+$/), Validators.maxLength(9)]], // Only integers allowed, max 9 digits
      sampleId: ['', [Validators.maxLength(2000)]], // Supports multiple values separated by comma, space, tab, or newline
      assayName: ['', [Validators.maxLength(200)]], // Assay names can be long
      testResultId: ['', [Validators.maxLength(500)]], // MaximumTestResultIdLength
      cassetteSerialNumber: ['', [Validators.maxLength(2000)]], // Textarea: supports multiple values separated by comma, space, tab, or newline
      cassetteLot: ['', [Validators.maxLength(2000)]], // Supports multiple values separated by comma, space, tab, or newline
    });

    // Savanna filters form for test results search
    this.savannaFiltersForm = this.fb.group({
      instrumentSerialNumber: ['', [Validators.maxLength(2000)]], // Textarea: supports multiple values separated by comma, space, tab, or newline
      startDate: [''],
      endDate: [''],
      assayId: ['', [Validators.pattern(/^\d+$/), Validators.maxLength(9)]], // Only integers allowed, max 9 digits
      sampleId: ['', [Validators.maxLength(2000)]], // Supports multiple values separated by comma, space, tab, or newline
      assayName: ['', [Validators.maxLength(200)]], // Assay names can be long
      testResultId: ['', [Validators.maxLength(500)]], // MaximumTestResultIdLength
      cartridgeSerialNumber: ['', [Validators.maxLength(2000)]], // Textarea: supports multiple values separated by comma, space, tab, or newline
      cartridgeLot: ['', [Validators.maxLength(2000)]], // Supports multiple values separated by comma, space, tab, or newline
    });
  }

  private loadBundleData(): void {
    const bundleId = this.route.snapshot.paramMap.get('id');
    if (bundleId) {
      this.isLoading = true;

      // Load bundle data
      this.apiService
        .getBundleById(+bundleId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (bundle: Bundle) => {
            // Check if this is an Advanced Bundle (BundleType = 2)
            if (bundle.bundleType !== 2) {
              this.router.navigate(['/bundles']);
              return;
            }

            // Create BundleAdvanced object using data from the Bundle
            this.bundleAdvanced = {
              bundleId: bundle.bundleId,
              description: bundle.description || '',
              deviceType: bundle.deviceType || 0,
              bundle: bundle,
            };

            // Set device type from the bundle data
            this.selectedDeviceType = this.bundleAdvanced.deviceType;

            // Set read-only display properties
            this.bundleDateCreated = bundle.dateCreated ? new Date(bundle.dateCreated) : null;
            this.bundleCreatedByName = this.accountService.getUserName(bundle.createdBy);

            // Populate bundle form
            this.bundleForm.patchValue({
              name: bundle.name,
              description: this.bundleAdvanced.description,
            });

            // Load saved test results from bundle.testResultIds (already included in GetById)
            if (bundle.testResultIds && bundle.testResultIds.trim() !== '') {
              const testResultIds = bundle.testResultIds.split(',').filter(id => id.trim() !== '');
              if (testResultIds.length > 0) {
                // Fetch full test result details from Elastic using testResultIds
                // This will populate sofia2SelectedTests or savannaSelectedTests
                this.fetchTestResultsFromElastic(testResultIds);
                // Don't set isLoading to false here, let fetchTestResultsFromElastic handle it
              } else {
                this.isLoading = false;
              }
            } else {
              this.isLoading = false;
            }
          },
          error: error => {
            console.error('Error loading bundle:', error);
            this.isLoading = false;
            // Navigate back to bundles list on error
            this.router.navigate(['/bundles']);
          },
        });
    } else {
      // No bundle ID provided, navigate back
      this.router.navigate(['/bundles']);
    }
  }

  /**
   * Fetch full test result details from Elastic using test result IDs
   * This populates sofia2SelectedTests or savannaSelectedTests for the third tab
   */
  private fetchTestResultsFromElastic(testResultIds: string[]): void {
    if (!testResultIds || testResultIds.length === 0) {
      this.isLoading = false;
      return;
    }

    // Build filters to query Elastic for these specific test result IDs
    // We need to query separately for Sofia2 and Savanna based on device type
    if (this.selectedDeviceType === 1) {
      // Sofia2
      const filters: Sofia2TestResultFilters = {
        testResultId: testResultIds.join(','),
        instrumentType: 'SL',
      };

      const request: Sofia2QueryElasticRequest = {
        filters: filters,
      };

      this.apiService
        .querySofia2TestResults(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (results: BundleTestResultResponse[]) => {
            // Map results to flat structure and populate selected tests for third tab
            this.sofia2SelectedTests = results.map(r => ({
              testResultId: r.testResultId,
              instrumentSerialNumber: r.instrumentSerialNumber,
              assayId: r.assayId,
              revision: r.revision,
              assayName: r.assayName,
              cassetteSerialNumber: r.cassetteSerialNumber,
              cassetteLotNumber: r.cassetteLotNumber,
              dateTimeUtc: r.dateTimeUtc,
              dateTime: r.dateTime,
              operatorId: r.operatorId,
              result: r.result,
              sampleId: r.sampleId ?? '',
            }));
            this.updateDataSources();
            this.isLoading = false;
          },
          error: error => {
            console.error('Error fetching Sofia2 test results from Elastic:', error);
            this.isLoading = false;
          },
        });
    } else if (this.selectedDeviceType === 2) {
      // Savanna
      const filters: SavannaTestResultFilters = {
        testResultId: testResultIds.join(','),
        instrumentType: 'WC',
      };

      const request: SavannaQueryElasticRequest = {
        filters: filters,
      };

      this.apiService
        .querySavannaTestResults(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (results: SavannaBundleTestResultResponse[]) => {
            // Populate selected tests for third tab
            this.savannaSelectedTests = results;
            this.savannaSelectedTestsDataSource.data = this.savannaSelectedTests;
            this.isLoading = false;
            this.updateDataSources();
          },
          error: error => {
            console.error('Error fetching Savanna test results from Elastic:', error);
            this.isLoading = false;
          },
        });
    } else {
      // Unknown device type
      this.isLoading = false;
    }
  }

  onSaveBundle(): void {
    if (this.bundleForm.valid) {
      const dialog = this.dialogService.openConfirmDialog(
        'Are you sure you want to save the changes to this Advanced Bundle?',
      );

      dialog.afterClosed().subscribe(res => {
        if (res) {
          this.isLoading = true;

          const formValue = this.bundleForm.value;

          // Update bundle data
          this.bundleAdvanced.bundle.name = formValue.name;
          this.bundleAdvanced.description = formValue.description;

          // Call the API to update the bundle
          this.apiService
            .updateBundleAdvanced(this.bundleAdvanced)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.isLoading = false;
                this.notificationService.success('Advanced Bundle updated successfully');
                // Navigate back to bundles list
                this.router.navigate(['/bundles']);
              },
              error: error => {
                console.error('Error updating bundle:', error);
                this.isLoading = false;
                this.notificationService.error('Failed to update Advanced Bundle');
              },
            });
        }
      });
    }
  }

  applyFilters(): void {
    // Reset to first page whenever filters are applied
    this.pageIndex = 0;
    // Get the appropriate form based on device type
    const activeForm =
      this.selectedDeviceType === 1 ? this.sofia2FiltersForm : this.savannaFiltersForm;

    if (activeForm.invalid) {
      this.notificationService.error('Please fill in all required filter fields.');
      return;
    }

    this.isQueryingResults = true;
    this.showResults = false;
    // Reset selection and search/sort when applying new filters
    this.selectedSofia2Ids.clear();
    this.selectedSavannaIds.clear();
    this.searchQuery = '';
    this.resultsSortState = { active: '', direction: '' };

    // Helper function to convert DeviceType ID to InstrumentType code
    // Sofia2 (id: 1) -> SL, Savanna (id: 2) -> WC
    const convertDeviceTypeToInstrumentType = (
      deviceTypeId: number | null | undefined,
    ): string | undefined => {
      if (!deviceTypeId || deviceTypeId <= 0) {
        return undefined;
      }

      const device = this.experimentService.devices.find(d => d.id === deviceTypeId);
      return device?.typeCode || undefined;
    };

    // Build the request with cleaned values from the active form
    const rawFilters = {
      instrumentSerialNumber: activeForm.get('instrumentSerialNumber')?.value,
      startDate: activeForm.get('startDate')?.value,
      endDate: activeForm.get('endDate')?.value,
      assayId: activeForm.get('assayId')?.value,
      sampleId: activeForm.get('sampleId')?.value,
      assayName: activeForm.get('assayName')?.value,
      testResultId: activeForm.get('testResultId')?.value,
      cassetteSerialNumber: activeForm.get('cassetteSerialNumber')?.value,
      cassetteLot: activeForm.get('cassetteLot')?.value,
      cartridgeSerialNumber: activeForm.get('cartridgeSerialNumber')?.value,
      cartridgeLot: activeForm.get('cartridgeLot')?.value,
      deviceType: this.selectedDeviceType,
    };

    // Convert deviceType to instrumentType
    const instrumentType = convertDeviceTypeToInstrumentType(rawFilters.deviceType);

    // Do not send sourceFields; API determines fields based on instrument type and returns a fixed model

    // Route to appropriate endpoint based on device type
    if (this.selectedDeviceType === 1) {
      // Sofia2 (DeviceType ID = 1)
      const filters = this.buildSofia2Filters(rawFilters, instrumentType);
      const request: Sofia2QueryElasticRequest = {
        filters: filters,
      };

      this.apiService
        .querySofia2TestResults(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: results => {
            // Flatten nested Sofia2 structure from API into flat model for the table
            this.sofia2Results = (results as any[]).map(r => {
              // Backend sends dateTime and dateTimeUtc both in UTC
              // dateTimeUtc: explicitly UTC (will be displayed as UTC)
              // dateTime: UTC value (will be displayed in local timezone by Angular date pipe)
              const dateTimeUtc = r.dateTimeUtc ? new Date(r.dateTimeUtc) : (r.dateTime ? new Date(r.dateTime) : new Date(0));
              const dateTime = r.dateTime ? new Date(r.dateTime) : (r.testRunDateTime ? new Date(r.testRunDateTime) : new Date(0));
              return {
                testResultId: r.testResultId,
                instrumentSerialNumber: r.instrumentSerialNumber,
                assayId: r.assayId,
                revision: r.assayRevision ?? r.revision,
                assayName: r.assayName,
                cassetteSerialNumber:
                  r.sofia2?.metaJson?.test_data?.cassette_data?.cassette_serial_number ??
                  r.cassetteSerialNumber,
                cassetteLotNumber:
                  r.sofia2?.testResultJson?.testResult?.cassetteLotNumber ?? r.cassetteLotNumber,
                dateTimeUtc: dateTimeUtc,
                dateTime: dateTime,
                operatorId: r.sofia2?.metaJson?.test_data?.user_data?.id ?? r.operatorId,
                result: r.QualitativeResults ?? r.result,
                sampleId: r.sampleId ?? '',
              };
            });
            this.sofia2ResultsDataSource.data = this.sofia2Results;
            // Reset pagination
            if (this.sofia2ResultsDataSource.paginator) this.sofia2ResultsDataSource.paginator.firstPage();

            this.showResults = true;
            this.isQueryingResults = false;
            this.updateDataSources();

            if (results.length === 0) {
              this.notificationService.info(
                'No test results found matching the specified filters.',
              );
            } else {
              this.notificationService.success(`Found ${results.length} test results.`);
            }
          },
          error: error => {
            console.error('Error querying Sofia2 test results:', error);
            this.isQueryingResults = false;
            const errorMessage =
              error.error?.message ||
              error.error?.errors ||
              'An error occurred while querying test results.';
            this.notificationService.error(errorMessage);
          },
        });
    } else if (this.selectedDeviceType === 2) {
      // Savanna (DeviceType ID = 2)
      const filters = this.buildSavannaFilters(rawFilters, instrumentType);
      const request: SavannaQueryElasticRequest = {
        filters: filters,
      };

      this.apiService
        .querySavannaTestResults(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (results: SavannaBundleTestResultResponse[]) => {
            // API already returns Savanna flat model with cartridge* fields
            this.savannaResults = results;
            this.savannaResultsDataSource.data = this.savannaResults;

            // Reset pagination
            if (this.savannaResultsDataSource.paginator) this.savannaResultsDataSource.paginator.firstPage();

            this.showResults = true;
            this.isQueryingResults = false;
            this.updateDataSources();

            if (results.length === 0) {
              this.notificationService.info(
                'No test results found matching the specified filters.',
              );
            } else {
              this.notificationService.success(`Found ${results.length} test results.`);
            }
          },
          error: error => {
            console.error('Error querying Savanna test results:', error);
            this.isQueryingResults = false;
            const errorMessage =
              error.error?.message ||
              error.error?.errors ||
              'An error occurred while querying test results.';
            this.notificationService.error(errorMessage);
          },
        });
    } else {
      // Fallback (should not happen, but handle gracefully)
      this.notificationService.error('Invalid device type. Only Sofia2 and Savanna are supported.');
      this.isQueryingResults = false;
      return;
    }
  }

  /**
   * Utilities: filtering and sorting
   */
  private normalizeValueForSearch(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (value instanceof Date) {
      return value.toISOString();
    }
    return String(value).toLowerCase();
  }

  private matchesSearch(record: any, query: string): boolean {
    if (!query) {
      return true;
    }
    const q = query.toLowerCase();
    return Object.values(record).some(v => this.normalizeValueForSearch(v).includes(q));
  }


  refreshResultFilters() {
    const searchKey = this.searchQuery ? this.searchQuery.trim().toLowerCase() : '';

    if (this.selectedDeviceType === 1) {
      this.sofia2ResultsDataSource.filter = searchKey;
      // If no global search, but column filters exist, trigger filter to respect them
      if (!searchKey && this.columnFilterService.getActiveTableFilters(this.resultsTableName as any)) {
        this.sofia2ResultsDataSource.filter = 'custom-filter';
      }
    } else {
      this.savannaResultsDataSource.filter = searchKey;
      if (!searchKey && this.columnFilterService.getActiveTableFilters(this.resultsTableName as any)) {
        this.savannaResultsDataSource.filter = 'custom-filter';
      }
    }
    this.tableDataChanged();
    this.pageIndex = 0;
  }

  onSearchChange(): void {
    this.refreshResultFilters();
  }

  onSelectedTestsSearchChange() {
    const searchKey = this.selectedTestsSearchQuery ? this.selectedTestsSearchQuery.trim().toLowerCase() : '';
    if (this.selectedDeviceType === 1) {
      this.sofia2SelectedTestsDataSource.filter = searchKey;
      if (!searchKey && this.columnFilterService.getActiveTableFilters(this.selectedTableName as any)) {
        this.sofia2SelectedTestsDataSource.filter = 'custom-filter';
      }
    } else {
      this.savannaSelectedTestsDataSource.filter = searchKey;
      if (!searchKey && this.columnFilterService.getActiveTableFilters(this.selectedTableName as any)) {
        this.savannaSelectedTestsDataSource.filter = 'custom-filter';
      }
    }
    this.tableDataChanged();
  }

  // Removed filteredSavannaResults getter

  /**
   * Sorted results after filtering
   */
  // Removed sorted getters

  /**
   * Get paginated results for display
   */
  // Removed paginated getters

  /**
   * Handle page event from paginator
   */
  /**
   * Handle tab change event
   */
  onTabChanged(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
    this.tableDataChanged();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  onResultsSortChange(sort: Sort): void {
    this.resultsSortState = { active: sort.active, direction: sort.direction };
    this.pageIndex = 0;
  }

  // Clear all active sorting criteria and reset to first page
  clearSorting(): void {
    this.resultsSortState = { active: '', direction: '' };
    this.pageIndex = 0;
  }

  /**
   * Selection helpers
   */
  isSelected(id: string | number): boolean {
    const key = String(id);
    return this.selectedDeviceType === 1
      ? this.selectedSofia2Ids.has(key)
      : this.selectedSavannaIds.has(key);
  }

  /**
   * Check if Add Test button should be disabled
   */
  isAddTestButtonDisabled(): boolean {
    if (this.selectedDeviceType === 1) {
      return this.selectedSofia2Ids.size === 0;
    } else {
      return this.selectedSavannaIds.size === 0;
    }
  }

  /**
   * Get the count of currently selected tests
   */
  getSelectedTestsCount(): number {
    if (this.selectedDeviceType === 1) {
      return this.selectedSofia2Ids.size;
    } else {
      return this.selectedSavannaIds.size;
    }
  }

  /**
   * Check if a test is already in the selected tests array
   */
  isTestAlreadyAdded(testResultId: string | number): boolean {
    const id = String(testResultId);
    if (this.selectedDeviceType === 1) {
      return this.sofia2SelectedTests.some(t => String(t.testResultId) === id);
    } else {
      return this.savannaSelectedTests.some(t => String(t.testResultId) === id);
    }
  }

  /**
   * Add a single test to the selected tests array
   */
  addSingleTestToTable(test: BundleTestResultResponse | SavannaBundleTestResultResponse): void {
    if (this.selectedDeviceType === 1) {
      const sofia2Test = test as BundleTestResultResponse;
      if (this.isTestAlreadyAdded(sofia2Test.testResultId)) {
        this.notificationService.info('This test is already in the selected tests table.');
        return;
      }
      this.sofia2SelectedTests = [...this.sofia2SelectedTests, sofia2Test];
      this.updateDataSources();
      // Deselect if it was selected
      this.selectedSofia2Ids.delete(String(sofia2Test.testResultId));
      this.notificationService.success('Test added to selected tests.');
    } else if (this.selectedDeviceType === 2) {
      const savannaTest = test as SavannaBundleTestResultResponse;
      if (this.isTestAlreadyAdded(savannaTest.testResultId)) {
        this.notificationService.info('This test is already in the selected tests table.');
        return;
      }
      this.savannaSelectedTests = [...this.savannaSelectedTests, savannaTest];
      this.updateDataSources();
      // Deselect if it was selected
      this.selectedSavannaIds.delete(String(savannaTest.testResultId));
      this.notificationService.success('Test added to selected tests.');
    }
  }

  /**
   * Toggle filter to show/hide already added tests
   */
  onToggleShowAlreadyAddedTests(checked: boolean): void {
    this.showAlreadyAddedTests = checked;
    this.pageIndex = 0; // Reset to first page when toggling filter
  }

  toggleRowSelection(id: string | number): void {
    const key = String(id);
    const set = this.selectedDeviceType === 1 ? this.selectedSofia2Ids : this.selectedSavannaIds;
    if (set.has(key)) {
      set.delete(key);
    } else {
      set.add(key);
    }
  }

  areAllCurrentFilteredSelected(): boolean {
    const list =
      this.selectedDeviceType === 1 ? this.sofia2ResultsDataSource.filteredData : this.savannaResultsDataSource.filteredData;
    const set = this.selectedDeviceType === 1 ? this.selectedSofia2Ids : this.selectedSavannaIds;
    if (list.length === 0) {
      return false;
    }
    // Only consider tests that are not already added
    const availableTests = list.filter((r: any) => !this.isTestAlreadyAdded(r.testResultId));
    if (availableTests.length === 0) {
      return false;
    }
    return availableTests.every((r: any) => set.has(String(r.testResultId)));
  }

  toggleSelectAllCurrentFiltered(): void {
    const list =
      this.selectedDeviceType === 1 ? this.sofia2ResultsDataSource.filteredData : this.savannaResultsDataSource.filteredData;
    const set = this.selectedDeviceType === 1 ? this.selectedSofia2Ids : this.selectedSavannaIds;
    // Only consider tests that are not already added
    const availableTests = list.filter((r: any) => !this.isTestAlreadyAdded(r.testResultId));
    if (availableTests.length === 0) {
      return;
    }
    const allSelected =
      availableTests.length > 0 &&
      availableTests.every((r: any) => set.has(String(r.testResultId)));
    if (allSelected) {
      availableTests.forEach((r: any) => set.delete(String(r.testResultId)));
    } else {
      availableTests.forEach((r: any) => set.add(String(r.testResultId)));
    }
  }

  /**
   * Get the start index of the current page for display
   */
  getPaginationStart(): number {
    const total =
      this.selectedDeviceType === 1 ? this.sofia2Results.length : this.savannaResults.length;
    return total > 0 ? this.pageIndex * this.pageSize + 1 : 0;
  }

  /**
   * Get the end index of the current page for display
   */
  getPaginationEnd(): number {
    const total =
      this.selectedDeviceType === 1 ? this.sofia2Results.length : this.savannaResults.length;
    if (total === 0) {
      return 0;
    }
    return Math.min((this.pageIndex + 1) * this.pageSize, total);
  }

  clearFilters(): void {
    if (this.selectedDeviceType === 1) {
      this.sofia2FiltersForm.reset();
    } else if (this.selectedDeviceType === 2) {
      this.savannaFiltersForm.reset();
    }
    this.sofia2Results = [];
    this.savannaResults = [];
    this.showResults = false;
    this.pageIndex = 0; // Reset pagination
    this.notificationService.info('Filters cleared.');
  }

  onCancel(): void {
    // Check if there are unsaved changes
    const filtersFormDirty =
      (this.selectedDeviceType === 1 && this.sofia2FiltersForm.dirty) ||
      (this.selectedDeviceType === 2 && this.savannaFiltersForm.dirty);
    const hasChanges = this.bundleForm.dirty || filtersFormDirty;

    if (hasChanges) {
      const dialog = this.dialogService.openConfirmDialog(
        'You have unsaved changes. Are you sure you want to cancel and return to the bundles list?',
      );

      dialog.afterClosed().subscribe(res => {
        if (res) {
          this.router.navigate(['/bundles']);
        }
      });
    } else {
      this.router.navigate(['/bundles']);
    }
  }

  /**
   * Prevent non-numeric characters and decimals in Assay ID input
   */
  onAssayIdKeyPress(event: KeyboardEvent): void {
    const char = String.fromCharCode(event.which || event.keyCode);
    // Allow only digits (0-9) and special keys (backspace, delete, tab, escape, enter, arrow keys)
    if (!/[0-9]/.test(char) && !this.isSpecialKey(event)) {
      event.preventDefault();
    }
  }

  /**
   * Remove any non-numeric characters that might have been pasted or entered
   */
  onAssayIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const activeForm =
      this.selectedDeviceType === 1 ? this.sofia2FiltersForm : this.savannaFiltersForm;
    const control = activeForm.get('assayId');

    if (control && input.value) {
      // Remove all non-digit characters
      const numericValue = input.value.replace(/[^\d]/g, '');
      if (input.value !== numericValue) {
        control.setValue(numericValue, { emitEvent: false });
      }
    }
  }

  /**
   * Check if the pressed key is a special key (backspace, delete, tab, etc.)
   */
  private isSpecialKey(event: KeyboardEvent): boolean {
    return (
      event.key === 'Backspace' ||
      event.key === 'Delete' ||
      event.key === 'Tab' ||
      event.key === 'Escape' ||
      event.key === 'Enter' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey
    );
  }

  /**
   * Build Sofia2 specific filters
   * For now, uses the same structure as Savanna, but separated for future customization
   */
  private buildSofia2Filters(
    rawFilters: any,
    instrumentType: string | undefined,
  ): Sofia2TestResultFilters {
    const cleanStringValue = (value: string | null | undefined): string | undefined => {
      if (!value || value.trim() === '') {
        return undefined;
      }
      return value.trim() || undefined;
    };

    // Helper function to clean Assay ID: validates and returns string (input is type="text" with numeric validation)
    // Only accepts integers (0-9), max 9 digits
    const cleanAssayIdValue = (value: number | string | null | undefined): string | undefined => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      // Convert to string and trim
      const stringValue = typeof value === 'number' ? String(value) : String(value).trim();
      // Validate it's a valid integer (only digits)
      if (stringValue === '' || !/^\d+$/.test(stringValue)) {
        return undefined;
      }
      return stringValue;
    };

    // Helper function to normalize multi-value fields: split by multiple separators and join with commas
    // Supports comma, space, tab, and newline separators for fields like serial numbers, sample IDs, and lot numbers
    const normalizeSerialNumberValue = (value: string | null | undefined): string | undefined => {
      if (!value || value.trim() === '') {
        return undefined;
      }
      // Split by commas, spaces, tabs, and newlines (both \n and \r\n)
      const values = value
        .split(/[,\s\t\n\r]+/)
        .map(v => v.trim())
        .filter(v => v !== '');

      if (values.length === 0) {
        return undefined;
      }

      // Join with commas (backend processes commas, spaces, tabs, and newlines)
      return values.join(',');
    };

    const cleanDateValue = (value: Date | string | null | undefined): Date | undefined => {
      if (!value) {
        return undefined;
      }
      if (value instanceof Date) {
        return value;
      }
      if (typeof value === 'string' && value.trim() !== '') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? undefined : date;
      }
      return undefined;
    };

    return {
      instrumentSerialNumber: normalizeSerialNumberValue(rawFilters.instrumentSerialNumber),
      startDate: cleanDateValue(rawFilters.startDate),
      endDate: cleanDateValue(rawFilters.endDate),
      assayId: cleanAssayIdValue(rawFilters.assayId),
      sampleId: normalizeSerialNumberValue(rawFilters.sampleId),
      assayName: cleanStringValue(rawFilters.assayName),
      testResultId: cleanStringValue(rawFilters.testResultId),
      cassetteSerialNumber: normalizeSerialNumberValue(rawFilters.cassetteSerialNumber),
      cassetteLot: normalizeSerialNumberValue(rawFilters.cassetteLot),
      instrumentType: instrumentType, // SL for Sofia2
      // Future Sofia2-specific fields can be added here
    };
  }

  /**
   * Add selected tests to the selected tests arrays
   */
  addSelectedTestsToTable(): void {
    if (this.selectedDeviceType === 1) {
      // Sofia2: Add tests that are checked in the filter table
      // Use filteredData to match user view
      const testsToAdd = this.sofia2ResultsDataSource.filteredData.filter((r: BundleTestResultResponse) =>
        this.selectedSofia2Ids.has(String(r.testResultId)),
      );

      // Avoid duplicates: only add tests that are not already in the selected tests array
      const existingIds = new Set(this.sofia2SelectedTests.map(t => String(t.testResultId)));
      const newTests = testsToAdd.filter(t => !existingIds.has(String(t.testResultId)));

      if (newTests.length > 0) {
        this.sofia2SelectedTests = [...this.sofia2SelectedTests, ...newTests];
        this.updateDataSources();
        // Remove added tests from selection
        newTests.forEach(t => this.selectedSofia2Ids.delete(String(t.testResultId)));
        this.notificationService.success(`Added ${newTests.length} test(s) to selected tests.`);
      } else {
        this.notificationService.info(
          'No new tests to add. All selected tests are already in the table.',
        );
      }

      // Clear all selections after adding (including any remaining)
      this.selectedSofia2Ids.clear();
    } else if (this.selectedDeviceType === 2) {
      // Savanna: Add tests that are checked in the filter table
      // Use filteredData
      const testsToAdd = this.savannaResultsDataSource.filteredData.filter(r =>
        this.selectedSavannaIds.has(String(r.testResultId)),
      );

      // Avoid duplicates: only add tests that are not already in the selected tests array
      const existingIds = new Set(this.savannaSelectedTests.map(t => String(t.testResultId)));
      const newTests = testsToAdd.filter(t => !existingIds.has(String(t.testResultId)));

      if (newTests.length > 0) {
        this.savannaSelectedTests = [...this.savannaSelectedTests, ...newTests];
        this.updateDataSources();
        // Remove added tests from selection
        newTests.forEach(t => this.selectedSavannaIds.delete(String(t.testResultId)));
        this.notificationService.success(`Added ${newTests.length} test(s) to selected tests.`);
      } else {
        this.notificationService.info(
          'No new tests to add. All selected tests are already in the table.',
        );
      }

      // Clear all selections after adding (including any remaining)
      this.selectedSavannaIds.clear();
    }
  }

  /**
   * Remove a test from the selected tests array
   */
  excludeTestFromSelected(testResultId: string | number): void {
    if (this.selectedDeviceType === 1) {
      this.sofia2SelectedTests = this.sofia2SelectedTests.filter(
        t => String(t.testResultId) !== String(testResultId),
      );
      this.updateDataSources();
      // Also remove from selection set if it was selected
      this.selectedSofia2SelectedTestIds.delete(String(testResultId));
      this.selectedTestsPageIndex = 0; // Reset to first page after removal
      this.notificationService.info('Test removed from selected tests.');
    } else if (this.selectedDeviceType === 2) {
      this.savannaSelectedTests = this.savannaSelectedTests.filter(
        t => String(t.testResultId) !== String(testResultId),
      );
      this.updateDataSources();
      // Also remove from selection set if it was selected
      this.selectedSavannaSelectedTestIds.delete(String(testResultId));
      this.selectedTestsPageIndex = 0; // Reset to first page after removal
      this.notificationService.info('Test removed from selected tests.');
    }
  }

  /**
   * Check if a test is selected in the selected tests table
   */
  isSelectedTestSelected(testResultId: string | number): boolean {
    const key = String(testResultId);
    return this.selectedDeviceType === 1
      ? this.selectedSofia2SelectedTestIds.has(key)
      : this.selectedSavannaSelectedTestIds.has(key);
  }

  /**
   * Toggle selection of a test in the selected tests table
   */
  toggleSelectedTestSelection(testResultId: string | number): void {
    const key = String(testResultId);
    const set =
      this.selectedDeviceType === 1
        ? this.selectedSofia2SelectedTestIds
        : this.selectedSavannaSelectedTestIds;

    if (set.has(key)) {
      set.delete(key);
    } else {
      set.add(key);
    }
  }

  /**
   * Check if all current filtered selected tests are selected
   */
  areAllCurrentSelectedTestsSelected(): boolean {
    const list =
      this.selectedDeviceType === 1
        ? this.sofia2SelectedTestsDataSource.filteredData
        : this.savannaSelectedTestsDataSource.filteredData;
    const set =
      this.selectedDeviceType === 1
        ? this.selectedSofia2SelectedTestIds
        : this.selectedSavannaSelectedTestIds;

    if (list.length === 0) {
      return false;
    }
    return list.every((r: any) => set.has(String(r.testResultId)));
  }

  /**
   * Toggle select all current filtered selected tests
   */
  toggleSelectAllSelectedTests(): void {
    const list =
      this.selectedDeviceType === 1
        ? this.sofia2SelectedTestsDataSource.filteredData
        : this.savannaSelectedTestsDataSource.filteredData;
    const set =
      this.selectedDeviceType === 1
        ? this.selectedSofia2SelectedTestIds
        : this.selectedSavannaSelectedTestIds;

    const allSelected =
      list.length > 0 && list.every((r: any) => set.has(String(r.testResultId)));

    if (allSelected) {
      list.forEach((r: any) => set.delete(String(r.testResultId)));
    } else {
      list.forEach((r: any) => set.add(String(r.testResultId)));
    }
  }

  /**
   * Get count of selected tests in the selected tests table
   */
  getSelectedTestsSelectionCount(): number {
    if (this.selectedDeviceType === 1) {
      return this.selectedSofia2SelectedTestIds.size;
    } else {
      return this.selectedSavannaSelectedTestIds.size;
    }
  }

  /**
   * Delete multiple selected tests from the selected tests array
   */
  deleteSelectedTestsFromSelected(): void {
    const count = this.getSelectedTestsSelectionCount();

    if (count === 0) {
      this.notificationService.info('No tests selected to delete.');
      return;
    }

    if (this.selectedDeviceType === 1) {
      this.sofia2SelectedTests = this.sofia2SelectedTests.filter(
        t => !this.selectedSofia2SelectedTestIds.has(String(t.testResultId)),
      );
      this.updateDataSources();
      this.selectedSofia2SelectedTestIds.clear();
      this.selectedTestsPageIndex = 0;
      this.notificationService.success(`Removed ${count} test(s) from selected tests.`);
    } else if (this.selectedDeviceType === 2) {
      this.savannaSelectedTests = this.savannaSelectedTests.filter(
        t => !this.selectedSavannaSelectedTestIds.has(String(t.testResultId)),
      );
      this.updateDataSources();
      this.selectedSavannaSelectedTestIds.clear();
      this.selectedTestsPageIndex = 0;
      this.notificationService.success(`Removed ${count} test(s) from selected tests.`);
    }
  }

  /**
   * Filtered selected tests after search
   */
  // Removed filteredSelected... getters. Using DataSource.

  // Removed filteredSelected... getters

  /**
   * Sorted selected tests after filtering
   */
  // Removed sortedSelected... getters

  /**
   * Get paginated selected tests for display
   */
  // Removed paginatedSelected... getters

  /**
   * Handle page event from selected tests paginator
   */
  onSelectedTestsPageChange(event: PageEvent): void {
    this.selectedTestsPageIndex = event.pageIndex;
    this.selectedTestsPageSize = event.pageSize;
  }

  /**
   * Sorting handler for selected tests tables
   */
  onSelectedTestsSortChange(sort: Sort): void {
    this.selectedTestsSortState = { active: sort.active, direction: sort.direction };
    this.selectedTestsPageIndex = 0;
  }

  clearSelectedTestsSorting(): void {
    this.selectedTestsSortState = { active: '', direction: '' };
    this.selectedTestsPageIndex = 0;
  }

  // Removed duplicate onSelectedTestsSearchChange implementation


  /**
   * Save selected tests to the bundle
   * Can save 0 test results to clear all test results from the bundle
   */
  onSaveSelectedTests(): void {
    if (!this.bundleAdvanced || !this.bundleAdvanced.bundleId) {
      this.notificationService.error('Bundle ID is missing. Cannot save test results.');
      return;
    }

    // Collect all test result IDs from selected tests
    const testResultIds: string[] = [];
    if (this.selectedDeviceType === 1) {
      testResultIds.push(...this.sofia2SelectedTests.map(t => String(t.testResultId)));
    } else if (this.selectedDeviceType === 2) {
      testResultIds.push(...this.savannaSelectedTests.map(t => String(t.testResultId)));
    }

    // Build confirmation message
    const message =
      testResultIds.length === 0
        ? 'Are you sure you want to clear all test results from this bundle?'
        : `Are you sure you want to save ${testResultIds.length} test result(s) to this bundle?`;

    const dialog = this.dialogService.openConfirmDialog(message);

    dialog.afterClosed().subscribe(res => {
      if (res) {
        this.isLoading = true;
        this.apiService
          .saveBundleTestResults(this.bundleAdvanced.bundleId, testResultIds)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.isLoading = false;
              if (testResultIds.length === 0) {
                this.notificationService.success(
                  'All test results have been cleared from the bundle.',
                );
              } else {
                this.notificationService.success(
                  `Successfully saved ${testResultIds.length} test result(s) to the bundle.`,
                );
              }
            },
            error: error => {
              console.error('Error saving test results:', error);
              this.isLoading = false;
              this.notificationService.error('Failed to save test results to the bundle.');
            },
          });
      }
    });
  }

  /**
   * Build Savanna specific filters
   * For now, uses the same structure as Sofia2, but separated for future customization
   */
  private buildSavannaFilters(
    rawFilters: any,
    instrumentType: string | undefined,
  ): SavannaTestResultFilters {
    const cleanStringValue = (value: string | null | undefined): string | undefined => {
      if (!value || value.trim() === '') {
        return undefined;
      }
      return value.trim() || undefined;
    };

    // Helper function to clean Assay ID: validates and returns string (input is type="text" with numeric validation)
    // Only accepts integers (0-9), max 9 digits
    const cleanAssayIdValue = (value: number | string | null | undefined): string | undefined => {
      if (value === null || value === undefined || value === '') {
        return undefined;
      }
      // Convert to string and trim
      const stringValue = typeof value === 'number' ? String(value) : String(value).trim();
      // Validate it's a valid integer (only digits)
      if (stringValue === '' || !/^\d+$/.test(stringValue)) {
        return undefined;
      }
      return stringValue;
    };

    // Helper function to normalize multi-value fields: split by multiple separators and join with commas
    // Supports comma, space, tab, and newline separators for fields like serial numbers, sample IDs, and lot numbers
    const normalizeSerialNumberValue = (value: string | null | undefined): string | undefined => {
      if (!value || value.trim() === '') {
        return undefined;
      }
      // Split by commas, spaces, tabs, and newlines (both \n and \r\n)
      const values = value
        .split(/[,\s\t\n\r]+/)
        .map(v => v.trim())
        .filter(v => v !== '');

      if (values.length === 0) {
        return undefined;
      }

      // Join with commas (backend processes commas, spaces, tabs, and newlines)
      return values.join(',');
    };

    const cleanDateValue = (value: Date | string | null | undefined): Date | undefined => {
      if (!value) {
        return undefined;
      }
      if (value instanceof Date) {
        return value;
      }
      if (typeof value === 'string' && value.trim() !== '') {
        const date = new Date(value);
        return isNaN(date.getTime()) ? undefined : date;
      }
      return undefined;
    };

    return {
      instrumentSerialNumber: normalizeSerialNumberValue(rawFilters.instrumentSerialNumber),
      startDate: cleanDateValue(rawFilters.startDate),
      endDate: cleanDateValue(rawFilters.endDate),
      assayId: cleanAssayIdValue(rawFilters.assayId),
      sampleId: normalizeSerialNumberValue(rawFilters.sampleId),
      assayName: cleanStringValue(rawFilters.assayName),
      testResultId: cleanStringValue(rawFilters.testResultId),
      cartridgeSerialNumber: normalizeSerialNumberValue(rawFilters.cartridgeSerialNumber),
      cartridgeLot: normalizeSerialNumberValue(rawFilters.cartridgeLot),
      instrumentType: instrumentType, // WC for Savanna
      // Future Savanna-specific fields can be added here
    };
  }
}
